import { useEffect, useRef, useState, type RefObject } from 'react';
import { keyboardInset, revealDelta } from './keyboardReveal';
import { isKeyboardOpen } from './screenFit';
import { isBraveIOS, isChromeIOS, isGoogleAppIOS, isIOS } from './platform';

interface Region {
  top: number;
  bottom: number;
}

const isField = (node: EventTarget | null): node is HTMLInputElement =>
  node instanceof HTMLInputElement;

const readInset = (restingHeight = window.innerHeight) =>
  keyboardInset({
    innerHeight: Math.max(window.innerHeight, restingHeight),
    viewportHeight: window.visualViewport?.height ?? window.innerHeight,
    viewportScale: window.visualViewport?.scale,
  });

/**
 * While an input inside `ref` has focus, scrolls the page (smoothly) just
 * enough to keep `regionOf(input)` inside the visual viewport. Nothing is
 * moved or resized: the page only scrolls.
 *
 * Returns extra scroll room while a field is focused (else 0). A fitted
 * page needs room below its content to raise a low field above the keyboard
 * or center it in the Google app's visible area.
 */
export function useKeyboardReveal(
  ref: RefObject<HTMLElement | null>,
  regionOf: (input: HTMLInputElement) => Region,
): number {
  const ios = isIOS();
  const googleIOS = isGoogleAppIOS();
  const criosIOS = isChromeIOS();
  const braveIOS = isBraveIOS();
  const restingHeight = useRef(window.innerHeight);
  const restingWidth = useRef(window.innerWidth);
  const editing = useRef(false);
  // The visual height last centered for this field. Google only recenters
  // when the keyboard gets taller than that, not on toolbar/scroll resizes.
  const centered = useRef<{ field: HTMLInputElement; height: number } | null>(
    null,
  );
  const [focused, setFocused] = useState<HTMLInputElement | null>(null);
  const [inset, setInset] = useState(readInset);
  const [keyboardOpen, setKeyboardOpen] = useState(() => readInset() > 0);
  const [viewportRevision, setViewportRevision] = useState(0);
  // Highest scroll room during this keyboard session. innerHeight collapses
  // while a focused field is dragged; shrinking the spacer jumps the page.
  const [roomFloor, setRoomFloor] = useState(0);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const onFocusIn = (e: FocusEvent) => {
      if (isField(e.target)) {
        if (!editing.current && readInset(restingHeight.current) === 0)
          restingHeight.current = window.innerHeight;
        editing.current = true;
        setKeyboardOpen(readInset(restingHeight.current) > 0);
        setFocused(e.target);
      }
    };
    const onFocusOut = (e: FocusEvent) => {
      // Field to field (Tab, the keyboard's arrows) keeps the room.
      const next = e.relatedTarget;
      if (isField(next) && root.contains(next)) return;
      editing.current = false;
      setFocused(null);
    };
    // Sample the DOM source as well as subscribing: a child can autofocus
    // before this parent effect subscribes.
    const syncFocus = () => {
      const current = document.activeElement;
      if (isField(current) && root.contains(current)) {
        editing.current = true;
        setFocused(current);
      }
    };
    syncFocus();
    root.addEventListener('focusin', onFocusIn);
    root.addEventListener('focusout', onFocusOut);
    return () => {
      root.removeEventListener('focusin', onFocusIn);
      root.removeEventListener('focusout', onFocusOut);
    };
  }, [ref]);

  useEffect(() => {
    let previousSize = '';
    const update = () => {
      if (!editing.current || restingWidth.current !== window.innerWidth) {
        restingHeight.current = window.innerHeight;
        restingWidth.current = window.innerWidth;
      }
      const visible = window.visualViewport;
      const size = [
        window.innerWidth,
        window.innerHeight,
        visible?.width,
        visible?.height,
        visible?.scale,
      ].join(':');
      // Google's caret panning can emit scroll/resize without changing the
      // visible size. Chasing those offsets creates a native/app scroll loop.
      if (googleIOS && size === previousSize) return;
      previousSize = size;
      // Safari 27 reports offsetTop equal to scrollY while the rects are
      // already visual. Keep the pre-edit height so a collapsing innerHeight
      // does not drop the spacer while the keyboard is still up.
      // Chrome inflates innerHeight past the pre-edit height during a drag
      // (684→792 in trace f441258c). max(live, resting) grows the spacer,
      // then the collapse snaps it back ~108px and the field jumps.
      const viewportHeight =
        window.visualViewport?.height ?? window.innerHeight;
      setInset(
        criosIOS && editing.current
          ? isKeyboardOpen({
              innerHeight: restingHeight.current,
              viewportHeight,
              viewportScale: window.visualViewport?.scale,
            })
            ? Math.round(restingHeight.current - viewportHeight)
            : 0
          : readInset(
              ios && !googleIOS ? restingHeight.current : window.innerHeight,
            ),
      );
      setKeyboardOpen(readInset(restingHeight.current) > 0);
      // Safari can pan, or finish a keyboard animation, without changing
      // the inset. Recheck visibility on those events too.
      setViewportRevision((revision) => revision + 1);
    };
    const visible = window.visualViewport;
    visible?.addEventListener('resize', update);
    if (!googleIOS && !criosIOS) visible?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    update();
    return () => {
      visible?.removeEventListener('resize', update);
      visible?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [googleIOS, criosIOS, ios]);

  // Visibility must not depend on detecting a keyboard. Some browsers shrink
  // innerHeight and visualViewport.height together, making the inset zero.
  // The actual visible bounds still tell us whether the field needs to move.
  // Google retains DOM focus after Done dismisses the keyboard. Do not
  // recenter on toolbar/overscroll resizes while that dormant focus remains.
  // Preserve the pre-edit height for keyboards that shrink both viewports.
  const active = focused != null && (!googleIOS || keyboardOpen);

  useEffect(() => {
    if (!active || !focused) {
      centered.current = null;
      return;
    }
    const viewHeight = window.visualViewport?.height ?? window.innerHeight;
    const previous = centered.current;
    const sameField = previous?.field === focused;
    // Trace 22663ee2: dragging a focused field resizes the layout viewport
    // and can grow the visual viewport as the toolbar hides. pageTop then
    // splits from scrollY. Neither one is a taller keyboard. Chrome on iOS
    // also swings the visual height by about 30px (363↔393) during a drag;
    // trace 37ee39de then smooth-scrolled -151px against that gesture. A
    // real keyboard open shrinks the viewport by hundreds of pixels.
    const shrinkNeeded = criosIOS ? 48 : 1;
    const keyboardGrew =
      sameField && previous.height - viewHeight > shrinkNeeded;
    if ((googleIOS || criosIOS) && sameField && !keyboardGrew) return;
    // After the room below the page has rendered, and once the keyboard
    // stops resizing the visual viewport (iOS fires several resizes).
    const timer = window.setTimeout(() => {
      const visible = window.visualViewport;
      const { top, bottom } = regionOf(focused);
      const height = visible?.height ?? window.innerHeight;
      centered.current = { field: focused, height };
      // Google reports getBoundingClientRect in the visual viewport. While
      // pageTop equals scrollY the old pageTop delta was 0. During a scroll
      // pageTop - scrollY equals offsetTop, and adding it scrolls an
      // on-screen field by that offset again. Safari 27 does the same:
      // offsetTop copies scrollY, so pageTop - scrollY is the visual origin
      // and is 0 for that trace. A classic pan still has pageTop = scrollY +
      // offsetTop, which keeps the old origin. Brave keeps pageTop near
      // 268 while scrollY returns to 0 and the field rect is already on
      // screen, so that origin repeats the same smooth scroll.
      const pageTop = visible?.pageTop;
      const viewTop =
        googleIOS || braveIOS
          ? 0
          : typeof pageTop === 'number'
            ? Math.max(0, pageTop - window.scrollY)
            : (visible?.offsetTop ?? 0);
      // Google's initial native reveal can leave the field against its
      // keyboard toolbar. Start at the reading position before any typing.
      const delta = googleIOS
        ? (top + bottom) / 2 - (viewTop + height / 2)
        : revealDelta({ top, bottom, viewTop, viewHeight: height });
      if (Math.abs(delta) < (googleIOS ? 2 : 1)) return;
      const reduce = window.matchMedia?.(
        '(prefers-reduced-motion: reduce)',
      ).matches;
      window.scrollBy({
        top: delta,
        // Avoid competing with Google's native animated caret reveal.
        behavior: googleIOS ? 'instant' : reduce ? 'auto' : 'smooth',
      });
    }, 60);
    return () => window.clearTimeout(timer);
  }, [
    active,
    focused,
    inset,
    regionOf,
    viewportRevision,
    googleIOS,
    braveIOS,
    criosIOS,
  ]);

  // Centering needs room even when the keyboard resizes both viewports and
  // reports no inset; otherwise the browser clamps the requested scroll.
  const centerRoom = googleIOS
    ? Math.ceil((window.visualViewport?.height ?? window.innerHeight) / 2)
    : 0;
  const offered = active ? Math.max(inset, centerRoom) : 0;
  if (googleIOS || criosIOS) {
    const nextFloor = active ? Math.max(roomFloor, offered) : 0;
    if (nextFloor !== roomFloor) setRoomFloor(nextFloor);
  }
  return (googleIOS || criosIOS) && active
    ? Math.max(roomFloor, offered)
    : offered;
}
